import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import { getTrainingModule } from 'http/trainingModules';
import { createTrainingPart, deleteTrainingPart, getPreviousTrainingPart } from 'http/trainingParts';
import { replaceMoodleTrainingPartLanguageLinks } from 'http/moodle';
import { routesAliases } from 'router/routesAliases';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ROLES } from 'appConstants';
import TrainingPartForm from '../TrainingPartForm';

const TRAINING_METHODS = ['theory', 'ojt', 'practical'];

const METHOD_TAB_INDEX = {
  theory: 1,
  practical: 2,
  ojt: 3,
};

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(5, 5),
  },
}));

const CreateTrainingPart = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const role = useSelector(state => state.auth.profile?.role);
  const isHyperAdmin = role === ROLES.HYPER;
  const isSuperAdmin = role === ROLES.SUPER;
  const { id: trainingModuleId, trainingMethod } = routerMatch.params;
  const [notFoundError, setNotFoundError] = useState(false);
  const [moduleData, setModuleData] = useState(null);
  const [previousTrainingPart, setPreviousTrainingPart] = useState(null);

  const backUrl = `${routesAliases.editTrainingModule}/${trainingModuleId}`;
  const backState = { activeTab: METHOD_TAB_INDEX[trainingMethod] ?? 0 };

  const isValidMethod = TRAINING_METHODS.includes(trainingMethod);
  const isSystemModule = Boolean(moduleData?.isFromSystem);
  const isReadOnly = isHyperAdmin ? false : isSuperAdmin ? isSystemModule : true;

  const [fetchState, fetchModule] = useAsyncFn(async moduleId => {
    const [moduleResult, previousResult] = await Promise.all([
      getTrainingModule(moduleId),
      getPreviousTrainingPart(moduleId, trainingMethod),
    ]);

    if (!moduleResult.error) {
      setModuleData(moduleResult);
      setNotFoundError(false);
    } else if (moduleResult.statusCode === 404) {
      setNotFoundError(true);
    }

    if (!previousResult?.error && !previousResult?.statusCode) {
      setPreviousTrainingPart(previousResult?.items?.[0] || null);
    } else {
      setPreviousTrainingPart(null);
    }

    return moduleResult;
  });

  useEffect(() => {
    if (isValidMethod) {
      setNotFoundError(false);
      setModuleData(null);
      setPreviousTrainingPart(null);
      fetchModule(trainingModuleId);
    }
  }, [trainingModuleId, trainingMethod, isValidMethod]);

  const breadCrumbsItems = useMemo(
    () => [
      {
        title: t(routeProps.parentTitle),
        link: routeProps.parentPath,
      },
      {
        title: moduleData?.name || t('routes.editTrainingModule'),
        link: { pathname: backUrl, state: backState },
      },
      {
        title: t('trainingParts.addPageTitle'),
        isActive: true,
      },
    ],
    [backState, backUrl, moduleData?.name, routeProps.parentPath, routeProps.parentTitle, t],
  );

  const [submittingState, createPart] = useAsyncFn(
    async payload => {
      const { moodleLanguageLinks, ...partPayload } = payload;
      const result = await createTrainingPart({
        ...partPayload,
        trainingModuleId: Number(trainingModuleId),
      });

      if (result.error || result.statusCode) {
        return result;
      }

      if (trainingMethod === 'theory' && moodleLanguageLinks?.variants?.length) {
        const linkResult = await replaceMoodleTrainingPartLanguageLinks(result.id, moodleLanguageLinks);
        if (linkResult?.error || linkResult?.statusCode) {
          await deleteTrainingPart(result.id);
          return linkResult;
        }
      }

      history.push(
        `${routesAliases.editTrainingPart}/${trainingModuleId}/training-parts/edit/${result.id}/${trainingMethod}`,
        backState,
      );
      return result;
    },
    [backState, backUrl, history, trainingMethod, trainingModuleId],
  );

  if (!isValidMethod || notFoundError || (moduleData && isReadOnly)) {
    return <NotFound />;
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || !moduleData}
    >
      {moduleData ? (
        <TrainingPartForm
          mode="create"
          layout="page"
          trainingMethod={trainingMethod}
          trainingModuleCode={moduleData.code}
          previousTrainingPart={previousTrainingPart}
          backUrl={backUrl}
          backState={backState}
          submitting={submittingState.loading}
          onSubmitForm={createPart}
          isReadOnly={isReadOnly}
        />
      ) : (
        <span />
      )}
    </PageLayout>
  );
};

CreateTrainingPart.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default CreateTrainingPart;
